/** @flow */

import { call, put, select, all, takeEvery } from "redux-saga/effects";
import {
  getInteractionsPage,
  getAdditionalInfoPage,
  getAbsenceStatisticsFilters,
  getAbsenceDocumentsPage,
  getAbsencesFilters,
  getAlertsTriggeredPage,
  getEmployeeAbsenceNotificationsPage,
} from "@modules/shared/selectors/employee-absences";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import { toast } from "react-toastify";
import actionCreators from "@modules/shared/actionCreator";
import {
  GetEmployeeAbsences,
  GetEmployeeAbsence,
  GetEmployeeAbsencesSchema,
  GetAlertsTriggered,
  CreateEmployeeAbsence,
  UpdateEmployeeAbsence,
  GetEmployeeAbsenceShiftPattern,
  GetEmployeeAbsenceShiftPatternOptions,
  UpdateEmployeeAbsenceShiftPattern,
  ExportEmployeeAbsenceHistory,
  ExportEmployeeAbsenceHistorySuccess,
  GetEmployeeInteractions,
  UpdateEmployeeInteraction,
  GetEmployeeAbsenceAdditionalInfo,
  UpdateEmployeeAbsenceAdditionalInfo,
  GetAbsenceStatistics,
  GetEmployeeAbsenceDocuments,
  GetRevisionHistory,
  GetEmployeeSMSMessages,
  CreateEmployeeAbsenceDocument,
  DeleteEmployeeAbsenceDocument,
  GetManagerNotes,
  ExportManagerNotes,
  GetEmployeeAbsenceNotification,
  GetEmployeeAbsenceNotifications,
  ResendEmployeeAbsenceNotification,
  GetAbsenceManagerNotesSchema,
  AddManagerNote,
  EditManagerNote,
  CancelRtwInterview,
  GetEmployeeNonCancelledAbsences,
  GetAbsenceNoteTypes,
  SubmitTransferAbsence,
  DeleteManagerNote,
  GetEmployeeAbsenceDocument,
  GetEmployeeBankHolidaysOptions,
} from "@modules/shared/constants/actions";
import { EMPLOYEE_QUESTION_TYPE } from "@modules/shared/constants/employee-absences";
import { downloadCsvFile, downloadFile } from "@sagas/utils";
import { dataURLtoFile } from "@utils/file";
import { transformFilters, transformSearchQuery, OPERATORS } from "@transformers/request";
import { sendSuccessMessage } from "@modules/app/actionCreator/index";
import { parseSchema } from "@utils/schema";

type EmployeeAbsencesRequest = $Exact<{
  clientId: number,
  employeeId: number,
  page?: number,
  isReportingRequest?: boolean,
  limit?: number,
}>;

export function* onGetEmployeeAbsences({
  payload,
}: Action<EmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  const filters = yield select(getAbsencesFilters);
  const transformedFilters = transformFilters(filters, {
    starts_at: OPERATORS.GREATER_THAN,
    rtw_at: OPERATORS.LESS_THAN,
  });

  try {
    const endpoint = payload.isReportingRequest
      ? `/api/v1/reports/client/${payload.clientId}/employee/${payload.employeeId}/absence-history`
      : `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence`;

    const response = yield call(http.get, endpoint, {
      params: {
        search: transformedFilters,
        page: payload.page,
      },
    });

    yield put(actionCreators.GetEmployeeAbsencesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsencesError(e.response));
  }
}

type EmployeeAbsenceRequest = $Exact<{
  ...EmployeeAbsencesRequest,
  absenceId: number,
}>;

export function* onGetEmployeeAbsence({
  payload,
}: Action<EmployeeAbsenceRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  try {
    const absenceId = payload.isReportingRequest
      ? payload.absenceId
      : transformFilters({ id: payload.absenceId });
    const endpoint = payload.isReportingRequest
      ? `/api/v1/reports/client/${payload.clientId}/employee/${payload.employeeId}/absence-history/${absenceId}`
      : `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence`;

    const response = yield call(http.get, endpoint);
    const absence = payload.isReportingRequest ? response.data : response.data.content[0];

    yield put(actionCreators.GetEmployeeAbsenceSuccess(absence));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceError(e.response));
  }
}

const absencesSchemaExcludedProperties = ["createdAt", "createdBy", "updatedAt", "updatedBy"];

export function* onGetEmployeeAbsencesSchema({
  payload,
}: Action<EmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }

  try {
    const schema = yield parseSchema("EmployeeAbsenceDTO", { clientId: payload.clientId });

    absencesSchemaExcludedProperties.forEach(
      excludedProperty => delete schema.properties[excludedProperty],
    );
    yield put(actionCreators.GetEmployeeAbsencesSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsencesSchemaSuccess(e.response));
  }
}

type EmployeeAbsenceDetailsRequest = $Exact<{
  ...EmployeeAbsencesRequest,
  absenceId: number,
  page?: number,
}>;

export function* onGetAlertsTriggered({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  try {
    const currentPage = yield select(getAlertsTriggeredPage);
    const page = (payload && payload.page) || currentPage;

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/alert`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetAlertsTriggeredSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAlertsTriggeredError(e.response));
  }
}

type CreateEmployeeAbsencesRequest = $Exact<{
  ...EmployeeAbsencesRequest,
  formData: Object,
  reloadListAfterUpdate?: boolean,
}>;

export function* onCreateEmployeeAbsence({
  payload,
}: Action<CreateEmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence`,
      payload.formData,
    );

    yield all([
      put(actionCreators.CreateEmployeeAbsenceSuccess(response.data)),
      put(actionCreators.ToggleAddAbsenceModal({ visible: false })),
      put(
        actionCreators.GetEmployeeAbsences({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(toast.success("Absence Added Successfully.")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateEmployeeAbsenceError(e.response));
  }
}

export function* onUpdateEmployeeAbsence({
  payload,
}: Action<CreateEmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.formData.id}`,
      payload.formData,
    );

    yield all([
      put(actionCreators.UpdateEmployeeAbsenceSuccess(response.data)),
      put(actionCreators.ToggleAddAbsenceModal({ visible: false })),
      payload.reloadListAfterUpdate &&
        put(
          actionCreators.GetEmployeeAbsences({
            clientId: payload.clientId,
            employeeId: payload.employeeId,
          }),
        ),
      put(
        actionCreators.GetRevisionHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.formData.id,
        }),
      ),
    ]);
    yield toast.success("Absence Updated Successfully.");
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeAbsenceError(e.response));
  }
}

export function* onGetEmployeeAbsenceShiftPattern({
  payload,
}: Action<EmployeeAbsenceRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/time-lost`,
    );

    yield all([
      put(actionCreators.GetEmployeeAbsenceShiftPatternSuccess(response.data)),
      put(
        actionCreators.GetRevisionHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceShiftPatternError(e.response));
  }
}

export function* onGetEmployeeAbsenceShiftPatternOptions({
  payload,
}: Action<EmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  try {
    const [schema, employeePositionList] = yield all([
      parseSchema("AbsenceTimeLostDTO", { clientId: payload.clientId }),
      call(
        http.get,
        `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      ),
    ]);

    const options = {
      schema,
      employeePosition: employeePositionList.data.content,
    };

    yield put(actionCreators.GetEmployeeAbsenceShiftPatternOptionsSuccess(options));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceShiftPatternOptionsError(e.response));
  }
}

type UpdateEmployeeDetailsRequest = $Exact<{
  ...EmployeeAbsenceDetailsRequest,
  formData: Array<Object>,
}>;

export function* onUpdateEmployeeAbsenceShiftPattern({
  payload,
}: Action<UpdateEmployeeDetailsRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.formData
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/time-lost`,
      payload.formData,
    );

    yield all([
      put(actionCreators.UpdateEmployeeAbsenceShiftPatternSuccess(response.data)),
      put(
        actionCreators.GetEmployeeAbsenceShiftPattern({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
      put(toast.success("Absence Shift Pattern Updated Successfully.")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeAbsenceShiftPatternError(e.response));
  }
}

export function* onGetEmployeeInteractions({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(getInteractionsPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/interaction`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetEmployeeInteractionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeInteractionsError(e.response));
  }
}

type UpdateEmployeeInteractionRequest = $Exact<{
  ...EmployeeAbsenceDetailsRequest,
  formData: Object,
  additionalInfo: Object,
}>;

export function* onUpdateEmployeeInteraction({
  payload,
}: Action<UpdateEmployeeInteractionRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.formData
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/interaction/${payload.formData.id}`,
      payload.formData,
    );

    yield all([
      put(sendSuccessMessage("GOODSHAPE_INTERACTION_UPDATED")),
      put(actionCreators.UpdateEmployeeInteractionSuccess(response.data)),
      put(
        actionCreators.GetEmployeeInteractions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeInteractionError(e.response));
  }
}

export function* onGetEmployeeAbsenceAdditionalInfo({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(getAdditionalInfoPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/dynamic-question-answer`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
        },
      },
    );

    let standardQuestions = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/standard-question-answer`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
        },
      },
    );

    standardQuestions = standardQuestions.data.content.map(item => ({
      ...item,
      question: {
        ...item.question,
        captureConsentToShare: false,
        type: EMPLOYEE_QUESTION_TYPE.STANDARD,
      },
    }));
    const mergedQuestions = [...response.data.content, ...standardQuestions].sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    yield put(
      actionCreators.GetEmployeeAbsenceAdditionalInfoSuccess({
        ...response.data,
        content: [...mergedQuestions],
      }),
    );
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceAdditionalInfoError(e.response));
  }
}

export function* onUpdateEmployeeAbsenceAdditionalInfo({
  payload,
}: Action<UpdateEmployeeInteractionRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.formData ||
    !payload.additionalInfo
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    const absenceQuestionType = (payload.additionalInfo.data.question || {}).type;
    const endpoint =
      absenceQuestionType === EMPLOYEE_QUESTION_TYPE.STANDARD
        ? `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/standard-question-answer/${payload.formData.id}`
        : `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/dynamic-question-answer/${payload.formData.id}`;

    const response = yield call(http.put, endpoint, payload.formData);

    yield all([
      put(sendSuccessMessage("ADDITIONAL_INFO_UPDATED")),
      put(actionCreators.UpdateEmployeeAbsenceAdditionalInfoSuccess(response.data)),
      put(
        actionCreators.GetEmployeeAbsenceAdditionalInfo({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeAbsenceAdditionalInfoError(e.response));
  }
}

type ExportAbsenceAbsenceHistoryRequest = $Exact<{
  clientId?: number,
  employee?: Object,
}>;

export function* onExportEmployeeAbsenceHistory({
  payload,
}: Action<ExportAbsenceAbsenceHistoryRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employee) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/employee/${payload.employee.id}/absence-history/export`,
    );

    yield put(
      actionCreators.ExportEmployeeAbsenceHistorySuccess({
        data: response.data,
        employee: payload.employee,
      }),
    );
  } catch (e) {
    yield put(actionCreators.ExportEmployeeAbsenceHistoryError(e.response));
  }
}

type DownloadAbsenceHistoryExportFileRequest = $Exact<{
  data: Object,
  employee: Object,
}>;

export function* onExportEmployeeAbsenceHistorySuccess({
  payload,
}: Action<DownloadAbsenceHistoryExportFileRequest>): Generator<*, void, *> {
  if (!payload || !payload.data || !payload.employee) {
    throw Error("Invalid payload provided");
  }

  yield downloadCsvFile(
    payload.data,
    `${payload.employee.firstName}-${payload.employee.lastName}-absence-history.csv`,
  );
}

type AbsenceStatisticRequest = $Exact<{
  clientId?: number,
  employeeId?: number,
  filters?: $Exact<{
    fromDate: string,
    toDate: string,
    absenceType: string,
  }>,
}>;

export function* onGetAbsenceStatistics({
  payload,
}: Action<AbsenceStatisticRequest>): Generator<*, void, *> {
  try {
    const filters =
      payload && payload.filters ? payload.filters : yield select(getAbsenceStatisticsFilters);
    const transformedFilters = {
      from_date: filters.fromDate,
      to_date: filters.toDate,
      absence_types: [filters.absenceType],
    };

    let client, data;

    if (
      payload &&
      payload.clientId &&
      payload.employeeId &&
      transformedFilters.from_date &&
      transformedFilters.to_date &&
      transformedFilters.absence_types
    ) {
      const response = yield call(
        http.post,
        `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/statistics`,
        transformedFilters,
      );
      data = response.data;
    } else {
      const response = yield call(
        http.post,
        `/api/v1/my-details/my-absence/statistics`,
        transformedFilters,
      );
      data = response.data;
    }
    yield put(actionCreators.GetAbsenceStatisticsSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceStatisticsError(e.response));
  }
}

type EmployeeAbsenceDocumentsRequest = $Exact<{
  clientId: number,
  absenceId: number,
  page?: number,
}>;

export function* onGetEmployeeAbsenceDocuments({
  payload,
}: Action<EmployeeAbsenceDocumentsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(getAbsenceDocumentsPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetEmployeeAbsenceDocumentsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceDocumentsError(e.response));
  }
}

export function* onGetRevisionHistory({ payload }: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/absence-revision-history`,
    );
    yield put(actionCreators.GetRevisionHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceDocumentsError(e.response));
  }
}

export function* onGetEmployeeSMSMessages({ payload }: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/sms-history`,
    );
    yield put(actionCreators.GetEmployeeSMSMessagesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeSMSMessagesError(e.response));
  }
}

type CreateEmployeeAbsenceDocumentRequest = $Exact<{
  clientId: number,
  absenceId: number,
  document: Object,
}>;

export function* onCreateEmployeeAbsenceDocument({
  payload,
}: Action<CreateEmployeeAbsenceDocumentRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId || !payload.document) {
    throw Error("Invalid payload provided");
  }

  try {
    const file = dataURLtoFile(payload.document.file);
    const data = new FormData();
    data.append("file", file.blob, decodeURIComponent(file.name));
    data.append("documentType", payload.document.documentType);

    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(actionCreators.CreateEmployeeAbsenceDocumentSuccess(response.data)),
      put(
        actionCreators.GetEmployeeAbsenceDocuments({
          clientId: payload.clientId,
          absenceId: payload.absenceId,
        }),
      ),
      put(toast.success("Document Added Successfully.")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateEmployeeAbsenceDocumentError(e.response));
  }
}

type DeleteEmployeeAbsenceDocumentRequest = $Exact<{
  clientId: number,
  absenceId: number,
  fileId: number,
}>;

export function* onDeleteEmployeeAbsenceDocument({
  payload,
}: Action<DeleteEmployeeAbsenceDocumentRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId || !payload.fileId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document/${payload.fileId}`,
    );

    yield all([
      put(actionCreators.DeleteEmployeeAbsenceDocumentSuccess(response.data)),
      put(
        actionCreators.GetEmployeeAbsenceDocuments({
          clientId: payload.clientId,
          absenceId: payload.absenceId,
        }),
      ),
      put(toast.success("Document Removed Successfully.")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteEmployeeAbsenceDocumentError(e.response));
  }
}

type EmployeeAbsenceDocumentRequest = $Exact<{
  ...DeleteEmployeeAbsenceDocumentRequest,
  fileName: string,
}>;

export function* onGetEmployeeAbsenceDocument({
  payload,
}: Action<EmployeeAbsenceDocumentRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId || !payload.fileId || !payload.fileName) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document/${payload.fileId}`,
      {
        responseType: "blob",
      },
    );

    downloadFile(response.data, `${payload.fileId} ${payload.fileName}`);

    yield all([put(actionCreators.GetEmployeeAbsenceDocumentSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceDocumentError(e.response));
  }
}

export function* onGetEmployeeAbsenceNotifications({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(getEmployeeAbsenceNotificationsPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/notification`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetEmployeeAbsenceNotificationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceNotificationsError(e.response));
  }
}

type EmployeeAbsenceNotificationRequest = $Exact<{
  ...EmployeeAbsenceDetailsRequest,
  notificationId: number,
  successMessage?: string,
}>;

export function* onGetEmployeeAbsenceNotification({
  payload,
}: Action<EmployeeAbsenceNotificationRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.notificationId
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/notification/${payload.notificationId}`,
    );

    yield all([
      put(actionCreators.GetEmployeeAbsenceNotificationSuccess(response.data)),
      put(
        actionCreators.ToggleEmployeeAbsenceNotificationModal({
          visible: true,
          notification: response.data,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetEmployeeAbsenceNotificationError(e.response));
  }
}

export function* onResendEmployeeAbsenceNotification({
  payload,
}: Action<EmployeeAbsenceNotificationRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.notificationId
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/notification/${payload.notificationId}/resend`,
    );

    yield all([
      put(actionCreators.ResendEmployeeAbsenceNotificationSuccess(response.data)),
      put(sendSuccessMessage("ABSENCE_NOTIFICATION_RESEND_SUCCESS")),
    ]);
  } catch (e) {
    yield put(actionCreators.ResendEmployeeAbsenceNotificationError(e.response));
  }
}

export function* onGetAbsenceManagerNotesSchema(): Generator<*, void, *> {
  try {
    const schema = yield parseSchema("AbsenceManagerNoteDTO");

    yield all([put(actionCreators.GetAbsenceManagerNotesSchemaSuccess(schema))]);
  } catch (e) {
    yield put(actionCreators.GetAbsenceManagerNotesSchemaError(e.response));
  }
}

export type ManagerNote = $Exact<{
  absenceId?: number,
  noteType: {
    id: number,
  },
  notes: string,
}>;

export function* onGetManagerNotes({ payload }: Object): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }
  const page = payload.page || 1;

  const query = transformSearchQuery(
    { ...payload },
    {
      absenceId: {
        name: "absence",
        operator: OPERATORS.EQUALS,
      },
      toDate: {
        name: "updated_at",
        operator: OPERATORS.LESS_THAN,
      },
      fromDate: {
        name: "updated_at",
        operator: OPERATORS.GREATER_THAN,
      },
    },
  );

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note`,
      {
        params: {
          search: query,
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetManagerNotesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetManagerNotesError(e.response));
  }
}

export function* onExportManagerNotes({ payload }: Object): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }
  const page = payload.page || 0;

  const query = transformSearchQuery(
    { ...payload },
    {
      absenceId: {
        name: "absence",
        operator: OPERATORS.EQUALS,
      },
      toDate: {
        name: "updated_at",
        operator: OPERATORS.LESS_THAN,
      },
      fromDate: {
        name: "updated_at",
        operator: OPERATORS.GREATER_THAN,
      },
    },
  );

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note/export`,
      {
        params: {
          search: query,
          page,
          limit: config.results100PerPage,
        },
      },
    );
    const fromDate = payload.fromDate ? `From ${payload.fromDate}` : "";
    const toDate = payload.toDate ? `To ${payload.toDate}` : "";

    downloadCsvFile(response.data, `Absence Notes ${fromDate} ${toDate}.csv`);
    yield put(actionCreators.ExportManagerNotesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportManagerNotesError(e.response));
  }
}

export function* onAddManagerNote({ payload }: Object): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.note) {
    throw Error("Invalid payload provided!");
  }
  try {
    yield call(
      http.post,
      `api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note`,
      payload.note,
    );

    yield all([
      put(sendSuccessMessage("NOTE_ADDED")),
      put(
        actionCreators.GetManagerNotes({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
      put(actionCreators.AddManagerNoteSuccess()),
      put(actionCreators.ToggleAddEditManagerNotesModal({ visible: false })),
    ]);
  } catch (e) {
    yield put(actionCreators.AddManagerNoteError(e.response));
  }
}

export function* onEditManagerNote({ payload }: Object): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.managerNoteId ||
    !payload.note
  ) {
    throw Error("Invalid payload provided!");
  }
  try {
    yield call(
      http.put,
      `api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note/${payload.managerNoteId}`,
      payload.note,
    );

    yield all([
      put(sendSuccessMessage("NOTE_UPDATED")),
      put(actionCreators.EditManagerNoteSuccess()),
      put(
        actionCreators.GetManagerNotes({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
      put(actionCreators.ToggleAddEditManagerNotesModal({ visible: false })),
    ]);
  } catch (e) {
    yield put(actionCreators.EditManagerNoteError(e.response));
  }
}

export function* onDeleteManagerNote({ payload }: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.noteId) {
    throw Error("Invalid payload provided");
  }

  try {
    yield call(
      http.delete,
      `api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note/${payload.noteId}`,
    );

    let acts = [
      put(actionCreators.ToggleDeleteManagerNoteModal({ item: null })),
      put(actionCreators.DeleteManagerNoteSuccess()),
      put(
        actionCreators.GetManagerNotes({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ];
    acts.push(put(sendSuccessMessage("NOTE_DELETED")));
    yield all(acts);
  } catch (e) {
    yield put(actionCreators.DeleteManagerNoteError(e.response));
  }
}

export function* onCancelRtwInterviewTask({
  payload,
}: Action<{ clientId: number, task: Object }>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.task) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.task.id}/cancel`,
      null,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );

    yield put(
      actionCreators.GetEmployeeAbsence({
        clientId: payload.clientId,
        employeeId: payload.task.employee.id,
        absenceId: payload.task.absenceId,
        isReportingRequest: false,
      }),
    );
  } catch (e) {
    yield put(actionCreators.CancelRtwInterviewError(e.response));
  }
}

export function* onSubmitTransferAbsence({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  absenceId: number,
  employeeToId: number,
  employeePositionToId: number,
}>): Generator<void, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.employeeId ||
      !payload.absenceId ||
      !payload.employeeToId ||
      !payload.employeePositionToId
    ) {
      throw Error("Invalid payload provided");
    }
    const { clientId, employeeId, absenceId, employeeToId, employeePositionToId } = payload;
    const response = yield call(
      http.post,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/transfer-absence`,
      {
        employee_to_id: employeeToId,
        employee_position_to_id: employeePositionToId,
      },
    );

    yield all([
      put(
        actionCreators.GetEmployeeAbsences({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(actionCreators.SubmitTransferAbsenceSuccess(response.body)),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitTransferAbsenceError(e.response));
  }
}

export function* onGetEmployeeNonCancelledAbsences({
  payload,
}: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  const { clientId, employeeId } = payload;

  try {
    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence?search=is_cancelled:false`,
    );
    yield put(actionCreators.GetEmployeeNonCancelledAbsencesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeNonCancelledAbsencesError(e.response));
  }
}

export function* onGetAbsenceNoteTypes(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `${config.endpointBaseUrl}api/v1/data/absence-note-type`);
    yield put(actionCreators.GetAbsenceNoteTypesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceNoteTypesError(e.response));
  }
}

export function* onGetEmployeeBankHolidaysOptions({
  payload,
}: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/client/${payload.clientId}/employee/${payload.employeeId}/data/bank-holidays`,
    );

    yield put(actionCreators.GetEmployeeBankHolidaysOptionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeBankHolidaysOptionsError(e.response));
  }
}

export default function* EmployeeAbsencesSaga(): Generator<*, void, *> {
  yield all([
    takeEvery(GetEmployeeAbsences, onGetEmployeeAbsences),
    takeEvery(GetEmployeeAbsence, onGetEmployeeAbsence),
    takeEvery(GetEmployeeAbsencesSchema, onGetEmployeeAbsencesSchema),
    takeEvery(GetAlertsTriggered, onGetAlertsTriggered),
    takeEvery(CreateEmployeeAbsence, onCreateEmployeeAbsence),
    takeEvery(UpdateEmployeeAbsence, onUpdateEmployeeAbsence),
    takeEvery(GetEmployeeAbsenceShiftPattern, onGetEmployeeAbsenceShiftPattern),
    takeEvery(GetEmployeeAbsenceShiftPatternOptions, onGetEmployeeAbsenceShiftPatternOptions),
    takeEvery(UpdateEmployeeAbsenceShiftPattern, onUpdateEmployeeAbsenceShiftPattern),
    takeEvery(ExportEmployeeAbsenceHistory, onExportEmployeeAbsenceHistory),
    takeEvery(ExportEmployeeAbsenceHistorySuccess, onExportEmployeeAbsenceHistorySuccess),
    takeEvery(GetEmployeeInteractions, onGetEmployeeInteractions),
    takeEvery(UpdateEmployeeInteraction, onUpdateEmployeeInteraction),
    takeEvery(GetEmployeeAbsenceAdditionalInfo, onGetEmployeeAbsenceAdditionalInfo),
    takeEvery(UpdateEmployeeAbsenceAdditionalInfo, onUpdateEmployeeAbsenceAdditionalInfo),
    takeEvery(GetAbsenceStatistics, onGetAbsenceStatistics),
    takeEvery(GetEmployeeAbsenceDocuments, onGetEmployeeAbsenceDocuments),
    takeEvery(GetRevisionHistory, onGetRevisionHistory),
    takeEvery(GetEmployeeSMSMessages, onGetEmployeeSMSMessages),
    takeEvery(CreateEmployeeAbsenceDocument, onCreateEmployeeAbsenceDocument),
    takeEvery(DeleteEmployeeAbsenceDocument, onDeleteEmployeeAbsenceDocument),
    takeEvery(GetEmployeeAbsenceDocument, onGetEmployeeAbsenceDocument),
    takeEvery(GetManagerNotes, onGetManagerNotes),
    takeEvery(ExportManagerNotes, onExportManagerNotes),
    takeEvery(GetEmployeeAbsenceNotification, onGetEmployeeAbsenceNotification),
    takeEvery(GetEmployeeAbsenceNotifications, onGetEmployeeAbsenceNotifications),
    takeEvery(ResendEmployeeAbsenceNotification, onResendEmployeeAbsenceNotification),
    takeEvery(GetAbsenceManagerNotesSchema, onGetAbsenceManagerNotesSchema),
    takeEvery(AddManagerNote, onAddManagerNote),
    takeEvery(EditManagerNote, onEditManagerNote),
    takeEvery(CancelRtwInterview, onCancelRtwInterviewTask),
    takeEvery(SubmitTransferAbsence, onSubmitTransferAbsence),
    takeEvery(GetEmployeeNonCancelledAbsences, onGetEmployeeNonCancelledAbsences),
    takeEvery(GetAbsenceNoteTypes, onGetAbsenceNoteTypes),
    takeEvery(DeleteManagerNote, onDeleteManagerNote),
    takeEvery(GetEmployeeBankHolidaysOptions, onGetEmployeeBankHolidaysOptions),
  ]);
}
